import { Box, BoxProps, ClickAwayListener, Paper, Popper, Stack, styled, TextField, useTheme } from '@mui/material'
import chroma from 'chroma-js'
import { SaturationSlider, HueSlider } from '@yamada-ui/color-picker'
import React, { useEffect, useState } from 'react'
import { Spacer1, Spacer2 } from 'spacer'

export const DEFAULT_COLORS = [
  '#D32F2F',
  '#F57C00',
  '#FFCA28',
  '#8BC34A',
  '#00ACC1',
  '#3F51B5',
  '#6A1B9A',
  '#D81B60',
  '#EF9A9A',
  '#FFCC80',
  '#FFE082',
  '#C5E1A5',
  '#80DEEA',
  '#9FA8DA',
  '#CE93D8',
  '#F48FB1',
]

const StyledBox = styled(Box)<BoxProps & { selected?: boolean }>(({ selected }) => ({
  width: '24px',
  height: '24px',
  borderRadius: '4px',
  cursor: 'pointer',
  margin: '6px',
  position: 'relative',
  '&::before': selected
    ? {
        content: '""',
        display: 'block',
        position: 'absolute',
        top: '-4px',
        left: '-4px',
        width: '32px',
        height: '32px',
        boxShadow:
          '0px 10px 14px -6px rgba(0, 0, 0, 0.20), 0px 22px 35px 3px rgba(0, 0, 0, 0.14), 0px 8px 42px 7px rgba(0, 0, 0, 0.12)',
        borderRadius: '4px',
        border: 'solid 1px rgba(255, 255, 255, 1)',
      }
    : {},
}))

const ColorBlock = ({
  color,
  selectedColor,
  onMouseDown,
}: {
  color: string
  selectedColor?: string | null
  onMouseDown?: (color: string) => void
}) => (
  <StyledBox
    sx={{
      backgroundColor: color,
    }}
    selected={color === selectedColor}
    onMouseDown={(event: React.MouseEvent) => {
      event.preventDefault()
      onMouseDown?.(color)
    }}
    data-testid={`color-${color}`}
  />
)

type HSV = [number, number, number]
const DEFAULT_HSV = [0, 0, 1] as HSV

type ColorPickerProps = {
  value?: string
  onChange?: React.ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>
  name?: string
  label?: string
  placeholder?: string
  error?: boolean
  helperText?: string
  fullWidth?: boolean
}
export const ColorPicker = ({
  error,
  fullWidth,
  helperText,
  label,
  name,
  onChange,
  placeholder,
  value,
}: ColorPickerProps) => {
  const theme = useTheme()
  const [color, setColor] = useState<string>('')
  const [hsv, setHsv] = useState<HSV>(DEFAULT_HSV)
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const [open, setOpen] = useState<boolean>(false)

  useEffect(() => {
    if (value) {
      setHsv(chroma.valid(value) ? chroma.hex(value).hsv() : DEFAULT_HSV)
      setColor(value)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const updateValues = (newHsv: HSV, newColor: string) => {
    setColor(newColor)
    setHsv(newHsv)
  }
  const handlePopper = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
    setOpen(!open)
  }

  const handlePopperClose = (event: Event | React.SyntheticEvent) => {
    if (anchorEl && !(anchorEl as HTMLElement).contains(event.target as Node)) {
      setOpen(false)
    }
  }

  const handleSwatchChange = (newColor: string): void => {
    const newHsv = chroma.hex(newColor).hsv()
    updateValues(newHsv, newColor)
    onChange?.({ target: { value: newColor, name } } as React.ChangeEvent<HTMLInputElement>)
  }

  const handleHueChange = (hue: number): void => {
    const newHSV: HSV = [...hsv]
    newHSV[0] = hue
    const newColor: string = chroma.hsv(...newHSV).hex()
    updateValues(newHSV, newColor)
    onChange?.({ target: { value: newColor, name } } as React.ChangeEvent<HTMLInputElement>)
  }

  const handleSaturationChange = (newHsv: HSV) => {
    const newColor = chroma.hsv(...newHsv).hex()
    updateValues(newHsv, newColor)
    onChange?.({ target: { value: newColor, name } } as React.ChangeEvent<HTMLInputElement>)
  }

  return (
    <>
      <TextField
        name={name}
        value={color}
        placeholder={placeholder}
        label={label}
        onClick={handlePopper}
        error={error}
        helperText={helperText}
        fullWidth={fullWidth}
        InputProps={{
          startAdornment: color ? (
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              style={{ marginRight: '8px' }}
            >
              <rect width="24" height="24" rx="4" fill={color} />
            </svg>
          ) : undefined,
        }}
      />
      <Popper open={open} anchorEl={anchorEl} sx={{ zIndex: 1333 }}>
        <ClickAwayListener onClickAway={handlePopperClose}>
          <Paper sx={{ p: '16px' }}>
            <Stack direction="row">
              {DEFAULT_COLORS.slice(0, 7).map((hex) => (
                <ColorBlock key={hex} color={hex} onMouseDown={handleSwatchChange} selectedColor={color} />
              ))}
            </Stack>
            <Stack direction={'row'}>
              {DEFAULT_COLORS.slice(8, 15).map((hex) => (
                <ColorBlock key={hex} color={hex} onMouseDown={handleSwatchChange} selectedColor={color} />
              ))}
            </Stack>
            <Spacer1 />
            <Box sx={{ height: '80px', width: '400px' }}>
              <SaturationSlider
                size="md"
                value={hsv}
                onChange={handleSaturationChange}
                innerProps={{
                  style: {
                    height: '80px',
                  },
                }}
                thumbProps={{
                  style: {
                    width: '16px',
                    height: '16px',
                    backgroundColor: 'transparent',
                    border: `2px solid ${theme.palette.common.white}`,
                    transform: 'translate(-8px, -6px)',
                    borderRadius: '50%',
                  },
                }}
              />
            </Box>
            <Spacer2 />
            <Box>
              <HueSlider
                style={{ width: '400px', height: '32px' }}
                value={hsv[0]}
                onChange={handleHueChange}
                thumbProps={{
                  style: {
                    width: '16px',
                    height: '16px',
                    backgroundColor: 'transparent',
                    border: `2px solid ${theme.palette.common.white}`,
                    margin: '9px 0',
                    borderRadius: '50%',
                  },
                }}
              />
            </Box>
          </Paper>
        </ClickAwayListener>
      </Popper>
    </>
  )
}
